import type { Types as CoreTypes } from '@cornerstonejs/core';
import type { SpectralCalibration } from '@/models/spectralDoppler';
import { clampToRegionBbox } from './calibrationMath';

interface VtkImageDataLike {
  worldToIndex: (p: CoreTypes.Point3) => CoreTypes.Point3;
  indexToWorld: (p: CoreTypes.Point3) => CoreTypes.Point3;
}

function getVtkImageData(
  viewport: CoreTypes.IViewport
): VtkImageDataLike | undefined {
  const imgData = (
    viewport as {
      getImageData?: () => { imageData?: unknown } | undefined;
    }
  ).getImageData?.();
  return imgData?.imageData as VtkImageDataLike | undefined;
}

/** World → image-pixel (column, row). Returns [0, 0] if the viewport's image is not yet ready. */
export function worldToPixel(
  viewport: CoreTypes.IViewport,
  world: CoreTypes.Point3
): [number, number] {
  const vtk = getVtkImageData(viewport);
  if (!vtk) return [0, 0];
  const idx = vtk.worldToIndex(world);
  return [idx[0], idx[1]];
}

/**
 * Image-pixel (column, row) → world. Returns null when the viewport's image
 * is not yet ready. Z is fixed to 0 (spectral images are 2D).
 */
export function pixelToWorld(
  viewport: CoreTypes.IViewport,
  px: number,
  py: number
): CoreTypes.Point3 | null {
  const vtk = getVtkImageData(viewport);
  if (!vtk) return null;
  const w = vtk.indexToWorld([px, py, 0] as CoreTypes.Point3);
  return [w[0], w[1], w[2]] as CoreTypes.Point3;
}

/**
 * Snap a world point to the spectral region's bbox edge (in image-pixel space)
 * and return it in world coords. Used during handle drag so the crosshair/handle
 * physically stops at the Doppler section boundary.
 *
 * No-ops (returns world unchanged) when the viewport's imageData isn't ready.
 */
export function clampWorldToRegionBbox(
  viewport: CoreTypes.IViewport,
  world: CoreTypes.Point3,
  calibration: Pick<
    SpectralCalibration,
    'bboxMinX' | 'bboxMinY' | 'bboxMaxX' | 'bboxMaxY'
  >
): CoreTypes.Point3 {
  const vtk = getVtkImageData(viewport);
  if (!vtk) return world;
  const idx = vtk.worldToIndex(world);
  const [cpx, cpy] = clampToRegionBbox(idx[0], idx[1], calibration);
  const clampedWorld = vtk.indexToWorld([
    cpx,
    cpy,
    idx[2] ?? 0,
  ] as CoreTypes.Point3);
  return [
    clampedWorld[0],
    clampedWorld[1],
    clampedWorld[2],
  ] as CoreTypes.Point3;
}

/**
 * Snap a world point's pixel-Y coordinate to the spectral region's baseline
 * (`referencePixelY0`) while preserving its pixel-X column. Returns the
 * input unchanged when the viewport's imageData isn't ready.
 */
export function snapWorldToBaseline(
  viewport: CoreTypes.IViewport,
  world: CoreTypes.Point3,
  calibration: Pick<SpectralCalibration, 'referencePixelY0'>
): CoreTypes.Point3 {
  const vtk = getVtkImageData(viewport);
  if (!vtk) return world;
  const idx = vtk.worldToIndex(world);
  const snapped = vtk.indexToWorld([
    idx[0],
    calibration.referencePixelY0,
    idx[2] ?? 0,
  ] as CoreTypes.Point3);
  return [snapped[0], snapped[1], snapped[2]] as CoreTypes.Point3;
}

/**
 * Clamp a world point to one side of the baseline. Side is purely geometric
 * (pixel-Y vs `referencePixelY0`) and intentionally has no relationship to
 * velocity sign — VTI tracing UX cares about whether the user is drawing
 * above or below the visual baseline, not which way `physicalDeltaY` encodes
 * direction. Pixel-Y grows downward, so:
 *   'positive' = above baseline = pixel-Y ≤ Y0
 *   'negative' = below baseline = pixel-Y ≥ Y0
 *
 * No-ops when the viewport's imageData isn't ready.
 */
export function clampWorldToBaselineSide(
  viewport: CoreTypes.IViewport,
  world: CoreTypes.Point3,
  calibration: Pick<SpectralCalibration, 'referencePixelY0'>,
  side: 'positive' | 'negative'
): CoreTypes.Point3 {
  const vtk = getVtkImageData(viewport);
  if (!vtk) return world;
  const idx = vtk.worldToIndex(world);
  const py =
    side === 'positive'
      ? Math.min(idx[1], calibration.referencePixelY0)
      : Math.max(idx[1], calibration.referencePixelY0);
  const snapped = vtk.indexToWorld([
    idx[0],
    py,
    idx[2] ?? 0,
  ] as CoreTypes.Point3);
  return [snapped[0], snapped[1], snapped[2]] as CoreTypes.Point3;
}
